import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '../../../environments/environment';

interface VersionPayload {
    version: string;
    commit: string;
    builtAt: string;
    environment: string;
}

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
    private readonly POLL_INTERVAL_MS = 5 * 60 * 1000;
    private readonly SNACKBAR_HOLD = 0;

    private baseline: VersionPayload | null = null;
    private notified = false;
    private subscription?: Subscription;

    constructor(
        private readonly http: HttpClient,
        private readonly snackBar: MatSnackBar,
        @Inject(PLATFORM_ID) private readonly platformId: object
    ) {}

    start(): void {
        if (!isPlatformBrowser(this.platformId)) return;
        if (this.subscription) return;

        this.subscription = timer(0, this.POLL_INTERVAL_MS)
            .pipe(
                switchMap(() =>
                    this.http
                        .get<VersionPayload>(`${environment.apiUrl}/v1/version`)
                        .pipe(catchError(() => of(null)))
                )
            )
            .subscribe((payload) => this.handle(payload));
    }

    stop(): void {
        this.subscription?.unsubscribe();
        this.subscription = undefined;
    }

    private handle(payload: VersionPayload | null): void {
        if (!payload) return;
        if (!this.baseline) {
            this.baseline = payload;
            return;
        }
        if (this.notified) return;

        const changed =
            payload.commit !== this.baseline.commit ||
            payload.version !== this.baseline.version ||
            payload.builtAt !== this.baseline.builtAt;
        if (!changed) return;

        this.notified = true;
        const ref = this.snackBar.open(
            'Hay una nueva versión disponible.',
            'Recargar',
            {
                duration: this.SNACKBAR_HOLD,
                horizontalPosition: 'right',
                verticalPosition: 'bottom',
            }
        );
        ref.onAction().subscribe(() => window.location.reload());
    }
}
